import { useEffect, useState } from 'react';
import { DataLayerObject } from '@dotcom/crown-ui';
import { useInitializationDataContext } from '../data-context';
import {
  clearDataLayerEvents,
  clearDataLayerModalEvents,
  clearDataLayerModalLevels,
} from './analytics-context-helpers';
import { getTag } from './analytics-context-tags';
import { AnalyticsTag, TagOptions } from './analytics-context-types';

export const useAnalytics = () => {
  const [dataLayerAvailable, setDataLayerAvailable] = useState(false);

  const { initializedDataState } = useInitializationDataContext();
  const { membershipStatus } = initializedDataState;

  useEffect(() => {
    const dataLayerCheckTimer = setInterval(() => {
      if (typeof window !== 'undefined' && window.dataLayer?.['siteID'] !== 'undefined') {
        setDataLayerAvailable(true);
      }
    }, 500);
    return () => {
      clearInterval(dataLayerCheckTimer);
    };
  }, []);

  const buildTag = (tagType: string, options?: TagOptions, keysToDelete?: string[]) => {
    const tag: AnalyticsTag = getTag(tagType, options || ({} as TagOptions));

    // Add Hallmark membership status to user tag for ALL product types (POD + eCards).
    // See PRCM-7675: previously this was gated on productType === 'D', which is why POD
    // tags never received CID / H+ info in the dataLayer.
    if (tag.user) {
      // Pull customerId (internal SFCC id) out of the spread and remap the enterprise
      // consumerID onto the analytics CID field. customerId is intentionally discarded.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { customerId, consumerID, ...membershipInfo } = membershipStatus;

      tag.user.profile = {
        ...tag.user.profile,
        ...membershipInfo,
        // Prefer the enterprise consumer ID as CID; fall back to whatever getUserTag put
        // there (sfcno cookie) so we still push something for unauthenticated users.
        ...(consumerID ? { CID: consumerID } : {}),
      };
    }

    clearDataLayerEvents();
    clearDataLayerModalEvents();
    clearDataLayerModalLevels();
    DataLayerObject({ dataObj: JSON.stringify(tag), keysToDelete: keysToDelete || [] });

    return tag;
  };

  const track = (trackType = 'page') => {
    if (window && window._satellite && typeof window._satellite.track === 'function') {
      window._satellite.track(trackType);
    }
  };

  return {
    dataLayerAvailable,
    buildTag,
    track,
  };
};
